import React from "react";
import { Link } from "react-router-dom";

import highRoofVanImg from "../../assets/images/Highroof/high-roof.webp";

export const highRoofDryVanForRentSections = [
  {
    type: "hrdv-hero",
    title:
      "High Roof Dry Van for Rent in Dubai and Sharjah — Spacious Vans for General Cargo Deliveries Across the UAE | Hire Today",
    paragraphs: [
      <>Chiller Fleet Transport provides high roof vans for rent in Dubai, offering tall interior cargo space with payload capacity from 1.4 to 1.8 tons for cartons, packaged goods, and commercial shipments across UAE delivery routes. Our high roof van for rent fleet covers general cargo movement where interior height and load volume determine vehicle selection — not temperature control. If your cargo needs temperature control instead, our <Link to="/services" className="internal-link">refrigerated transport services</Link> cover chilled and frozen requirements separately.</>,
      "Our fleet of 28 high roof dry vans operating in Dubai and Sharjah branches are available for both scheduled and same-day dry van rental — assigned based on your cargo volume, load dimensions, and delivery route in both emirates.",
      "Contact our team to arrange high roof van rental in Dubai or Sharjah based on your cargo type and delivery schedule.",
    ],
  },

  {
    type: "hrdv-apart",
    title:
      "What Sets Our High Roof Dry Van Apart From Standard Cargo Vans",
    description:
      "Our high roof van for rent is built around one core advantage over standard cargo vans — usable interior space that standard roof configurations simply cannot match for commercial cargo movement. Three specific differences make this distinction operationally significant:",
    methods: [
      {
        label: "Interior Loading Height Advantage",
        description:
          "Standard cargo vans provide interior heights of approximately 1.3 to 1.5 meters — limiting vertical stacking and forcing horizontal cargo compensation that reduces overall load efficiency per trip. Our high roof dry vans provide interior heights of 1.9 to 2.1 meters, allowing full vertical stacking of cartons, racks, and tall commercial items that standard vans cannot accommodate without cargo modification or disassembly.",
      },
      {
        label: "Volume Efficiency Over Multiple Trips",
        description:
          "A high roof van carries significantly more cargo volume per trip than a standard roof van of the same length — reducing the number of trips required for the same delivery run and lowering per-unit delivery cost for businesses moving packaged goods at commercial volume. Our cargo van rental fleet is structured around that volume efficiency as the primary operational benefit.",
      },
      {
        label: "Loading Dock Compatibility",
        description:
          "The taller interior profile of our high roof vans aligns more effectively with standard commercial loading dock heights in Dubai and Sharjah warehouses — reducing manual cargo handling time at loading and unloading points compared to standard roof alternatives where height mismatch creates handling inefficiency.",
      },
    ],
  },

  {
    type: "hrdv-uses",
    title: "Commercial Uses of Our High Roof Dry Van Fleet",
    description:
      "Our high roof van for rent covers six primary commercial cargo scenarios where interior height and load volume determine vehicle selection over any other factor:",
    cargoTypes: [
      {
        title: "Carton Delivery",
        description:
          "Commercial carton delivery throughout Dubai and Sharjah retail zones depends on stacking height determining how many cartons fit per trip. Our high roof interior allows full vertical carton stacking that standard vans cannot accommodate at the same load volume — covering more retail stops per run without splitting loads across multiple vehicles.",
      },
      {
        title: "Furniture & Fixtures",
        description:
          "Tall furniture items, shelving units, and commercial fixtures that exceed standard van interior height require high roof van space for upright transport without disassembly. This saves handling time at both loading and delivery points all over Dubai and Sharjah commercial properties.",
      },
      {
        title: "Exhibition & Event Cargo",
        description:
          "Exhibition materials, display stands, banners, and event setup equipment all over Dubai event venues require tall interior clearance that standard delivery vans cannot provide. Our fleet covers event cargo movement on fixed schedules where vehicle readiness determines setup timing.",
      },
      {
        title: "Retail Restocking",
        description:
          "Packaged retail goods, display units, and commercial inventory moving from warehouses to retail outlets in Dubai require dry van rental where cargo volume per trip determines distribution efficiency — not temperature control.",
      },
      {
        title: "E-Commerce Distribution",
        description:
          "High-volume e-commerce parcel movement through out Dubai and Sharjah urban delivery zones where interior volume rather than payload weight determines how many orders fit per vehicle per route. Our high roof configuration maximizes parcel capacity per trip in dense urban delivery corridors.",
      },
      {
        title: "Office & Commercial Relocation",
        description:
          "Office equipment, server racks, filing systems, and commercial furniture requiring tall interior clearance for upright transport between commercial properties in Dubai — where disassembly at loading adds cost and time that high roof clearance eliminates entirely.",
      },
    ],
  },

  {
    type: "hrdv-fleet-spec",
    title:
      "High Roof Dry Van Fleet Specifications & Load Configuration",
    description:
      "Our cargo van rental fleet is built entirely on Toyota Hiace High Roof models — maintaining consistent interior dimensions and load configuration throughout every booking without variation between units. Every specification below applies to all 28 vehicles in our active fleet:",
    image: highRoofVanImg,
    imageAlt:
      "High roof dry van parked for general cargo delivery across Dubai and Sharjah",
    specRows: [
      { spec: "Interior Height", detail: "1.9 to 2.1 meters" },
      { spec: "Payload Capacity", detail: "1.4 to 1.8 tons" },
      { spec: "Primary Model", detail: "Toyota Hiace High Roof" },
      {
        spec: "Cargo Body",
        detail: "Dry enclosed box body — no refrigeration unit",
      },
      { spec: "Door Configuration", detail: "Both rear and side door access" },
      { spec: "GPS", detail: "Fitted as standard in our full fleet" },
      { spec: "RTA Compliance", detail: "Full fleet verified" },
      {
        spec: "Roof Type",
        detail:
          "High roof configuration in entire fleet — no standard roof variants in this category",
      },
    ],
    note: "All 28 high roof dry vans are maintained by in-house mechanics at our Dubai and Sharjah branches — keeping every unit operationally ready without third-party service dependency affecting fleet availability.",
  },

  {
    type: "hrdv-pricing",
    title: "Our High Roof Dry Van Rental Rates in Dubai & Sharjah",
    descriptionMain:
      "Our high roof dry van rental rates are determined strictly by two variables: your required rental duration and whether you need a dedicated delivery driver. Because these vehicles are configured for general, non-refrigerated freight, no temperature surcharges apply—the rate remains fixed regardless of the cargo type you are moving. Every rental price includes real-time GPS tracking and a full pre-dispatch mechanical check as standard, ensuring your vehicle is road-ready before it leaves our branch.",
    rateRows: [
      {
        duration: "Daily",
        withoutDriver: "AED 200 — 250",
        withDriver: "AED 350 — 400",
      },
      {
        duration: "Weekly",
        withoutDriver: "AED 1,200 — 1,500",
        withDriver: "AED 2,100 — 2,500",
      },
      {
        duration: "Monthly",
        withoutDriver: "AED 3,500 — 4,200",
        withDriver: "AED 5,500 — 6,500",
      },
    ],
    descriptionNote:
      "Standard 5% UAE VAT applies to all rental agreements. Inter-emirate routes extending beyond Dubai and Sharjah boundaries are subject to a standard route surcharge confirmed at the time of booking.",
  },

  {
    type: "hrdv-coverage",
    title: "Key Coverage Areas throughout Dubai & Sharjah",
    image: highRoofVanImg,
    imageAlt:
      "High roof dry van covering commercial delivery zones across Dubai and Sharjah",
    paragraphs: [
      "Our high roof van for rent in Dubai covers commercial delivery zones like Deira, Bur Dubai, Al Quoz, Business Bay, Jumeirah, Dubai Investment Park, and Al Barsha — where retail restocking, warehouse-to-outlet distribution, and urban commercial cargo movement in dense commercial corridors define the primary operational demand for high roof van capacity.",
      "Dry van rental Sharjah operations cover Sharjah city centre, Sharjah Industrial Area, Muwailih, and Al Nahda — where commercial cargo moving between Sharjah manufacturing zones and retail distribution points requires consistent vehicle availability and interior volume capacity that standard roof vans cannot match across the emirate's active commercial corridors.",
      "Businesses running general cargo both in Dubai and Sharjah simultaneously are handled under a single booking coordinated between our Ras Al Khor and Sharjah branches — covering cross-emirate commercial delivery without separate arrangements for each emirate.",
    ],
  },

  {
    type: "hrdv-why-choose",
    title:
      "Why Businesses in Dubai & Sharjah Choose Our High Roof Dry Van Fleet",
    description:
      "Businesses in Dubai and Sharjah that depend on consistent general cargo delivery return to operators whose fleet is maintained, correctly sized, and available without last-minute gaps. Here is what our high roof dry van operation is built around:",
    reasons: [
      {
        title: "28 Dedicated Dry Vans",
        description:
          "Our 28 high roof dry vans operate as a dedicated general cargo fleet — completely separate from our refrigerated vehicle allocation. This separation means dry van availability is never affected by refrigerated fleet demand regardless of how busy peak periods get in both emirates.",
      },
      {
        title: "Toyota Hiace High Roof Standard",
        description:
          "Every dry van in our fleet is a Toyota Hiace High Roof — not a mixed model arrangement where interior dimensions vary between bookings. Consistent model in all 28 units means the interior height and load configuration a client confirms at booking is exactly what arrives at loading.",
      },
      {
        title: "Same-Day Availability",
        description:
          "Standby high roof dry vans are maintained at both our Ras Al Khor and Sharjah branches for urgent same-day general cargo requirements — deployed without pulling vehicles from already-confirmed scheduled operations across either branch.",
      },
      {
        title: "In-House Vehicle Maintenance",
        description:
          "Both Dubai and Sharjah branches maintain their own mechanics for dry van servicing and maintenance. No third-party repair dependency means vehicle turnaround and availability remain within our operational control rather than external workshop schedules.",
      },
    ],
  },

  {
    type: "hrdv-faq",
    title: "FAQ's",
    faqs: [
      {
        q: "What cargo types are your high roof dry vans suitable for?",
        a: <>Cartons, packaged goods, furniture, exhibition materials, retail inventory, e-commerce parcels, and commercial equipment — any non-perishable general cargo where interior volume and height determine vehicle selection rather than temperature control. For temperature-sensitive cargo, our <Link to="/chiller-van-for-rent" className="internal-link">chiller van for rent in Dubai</Link> is the appropriate choice instead.</>,
      },
      {
        q: "What is the payload capacity of your high roof dry vans?",
        a: "Our high roof dry vans carry payload capacities from 1.4 to 1.8 tons across Dubai and Sharjah delivery routes — covering light to medium commercial cargo loads without requiring truck-scale vehicles for smaller volume operations.",
      },
      {
        q: "Do your high roof dry vans come with a driver?",
        a: "Both driver-included and self-drive options are available across our fleet. Self-drive requires a valid UAE driving license. Driver-included arrangements cover route coordination and loading assistance throughout the delivery.",
      },
      {
        q: "What is the minimum rental period for a high roof dry van?",
        a: "Daily rental is the minimum period available. Weekly and monthly arrangements are offered at progressively lower per-day rates for businesses running regular commercial cargo delivery cycles across Dubai or Sharjah.",
      },
      {
        q: "Can your high roof dry vans handle cross-emirate deliveries between Dubai and Sharjah?",
        a: "Yes. Cross-emirate dry van delivery between Dubai and Sharjah is coordinated between our Ras Al Khor and Sharjah branches under a single booking arrangement — no separate contracts required for each emirate.",
      },
    ],
  },

  {
    type: "cta",
    title:
      "Book a High Roof Dry Van for Rent for Your Next Commercial Delivery",
    paragraphs: [
      <>Our high roof dry van fleet across Dubai and Sharjah is ready for your next general cargo booking. Call or WhatsApp <Link to="/contact-us" className="internal-link">our team</Link> directly with your cargo volume and interior space requirement — vehicle availability and dispatch timing confirmed from there.</>,
    ],
  },
];
